import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Info } from 'lucide-react'

/**
 * Taxas da BeWave, em UM lugar só.
 *
 * Os percentuais NAO ficam aqui: chegam prontos do backend (PlatformFeeTable),
 * a mesma fonte que calcula o valor liquido de cada pagamento. Foi justamente
 * a duplicacao desses numeros pela interface que deixou /settings anunciando
 * "4,07% e 30 dias" muito depois de a tabela real ter mudado.
 */

export interface FeeRow {
  label: string
  rate: number
}

/**
 * Sem coluna de "liquidacao" de proposito: o prazo do gateway ate a BeWave nao
 * muda a data em que a escola recebe. Quem manda nisso e a quinzena da compra.
 */
export function PlatformFeesTable({ fees }: { fees: FeeRow[] }) {
  return (
    <div className="rounded-lg border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Forma de pagamento</TableHead>
            <TableHead className="text-right">Taxa</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fees.map((row) => (
            <TableRow key={row.label}>
              <TableCell className="font-medium">{row.label}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {row.rate.toFixed(2).replace('.', ',')}%
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

const STEPS = [
  'O aluno compra o pacote pelo aplicativo e paga com PIX, debito ou cartao de credito.',
  'A venda entra na quinzena da data da compra: dias 01 a 15, ou dia 16 ate o fim do mes. A forma de pagamento nao muda isso.',
  'A quinzena fecha e a BeWave desconta a taxa da tabela acima.',
  'O valor liquido cai na conta da escola 15 dias depois do fechamento — quinzena 01 a 15 no dia 30, quinzena 16 ao fim do mes no dia 15 seguinte.',
]

export function PlatformFeesExplainer() {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border p-4 space-y-3">
        {STEPS.map((text, index) => (
          <div key={text} className="flex items-start gap-3">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex-shrink-0 mt-0.5">
              {index + 1}
            </span>
            <p className="text-sm text-muted-foreground">{text}</p>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-900 p-3">
        <p className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">
            Por que 15 dias:
          </span>{' '}
          e o prazo do repasse quinzenal, igual para PIX, debito e credito. A
          espera vai de 15 dias (venda no ultimo dia da quinzena) a 30 dias
          (venda no primeiro dia) — a BeWave assume o prazo do cartao para que a
          escola nao precise esperar por ele.
        </p>
      </div>
    </div>
  )
}

/** Taxas num dialog — para telas onde isso e contexto, nao o assunto principal. */
export function PlatformFeesDialog({
  fees,
  triggerLabel = 'Ver taxas e prazos',
}: {
  fees: FeeRow[]
  triggerLabel?: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Info className="h-4 w-4 mr-2" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Taxas e prazos do pagamento pelo aplicativo</DialogTitle>
          <DialogDescription>
            A taxa e unica por forma de pagamento e ja esta aplicada sobre o
            valor bruto da transacao.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          <PlatformFeesTable fees={fees} />
          <PlatformFeesExplainer />
          <p className="text-xs text-muted-foreground">
            Pagamentos registrados pela escola (dinheiro, transferencia) nao
            passam pelo gateway, nao tem taxa e nao entram no repasse.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
